import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface RetroLoadingScreenProps {
    onLoadComplete: () => void;
}

const RetroLoadingScreen: React.FC<RetroLoadingScreenProps> = ({ onLoadComplete }) => {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState('');
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState<string[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  const [forceDesktop, setForceDesktop] = useState(false);
  const dotsInterval = useRef<NodeJS.Timeout | null>(null);
  
  // List of all possible messages
  const allMessages = [
    "Initializing system components...",
    "Memory check: PASSED",
    "Loading core modules: COMPLETE",
    "Starting VK-OS 08 kernel...",
    "Mounting system drives...",
    "Initializing UI components: PASSED",
    "Checking hardware compatibility: OK",
    "Loading user preferences: COMPLETE",
    "Preparing project database...",
    "Building portfolio assets: IN PROGRESS",
    "Optimizing display settings: DONE",
    "Finalizing system startup...",
    "System ready. Launching portfolio..."
  ];
  
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check if URL has /d or /desktop
    const path = window.location.pathname.toLowerCase();
    if (path.includes('/d') || path.includes('/desktop') || path.includes('/pc')) {
      setForceDesktop(true);
    }
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Create blinking dots animation
  useEffect(() => {
    dotsInterval.current = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    
    return () => {
      if (dotsInterval.current) clearInterval(dotsInterval.current);
    };
  }, []);
  
  // Initialize visible messages
  useEffect(() => {
    // Start with first 4 messages
    setVisibleMessages(allMessages.slice(0, 4));
  }, []);
  
  // Combined loading progress and message display
  useEffect(() => {
    const totalDuration = 1000; // 1 second total loading time
    const totalSteps = 100; // 100% progress
    const stepTime = totalDuration / totalSteps;
    
    // Calculate how many remaining messages we need to show
    const remainingMessages = allMessages.length - 4;
    
    // If we have 9 remaining messages and 100 progress steps,
    // we want to show a new message every ~11 progress steps
    const messageProgressInterval = remainingMessages > 0 ? 
      Math.floor(totalSteps / remainingMessages) : totalSteps;
    
    let currentProgress = 0;
    let messageIndex = 4; // Start with the 5th message (index 4)
    
    const interval = setInterval(() => {
      currentProgress++;
      
      // Update progress
      setProgress(currentProgress);
      
      // Check if we need to show a new message
      if (messageIndex < allMessages.length && 
          currentProgress % messageProgressInterval === 0) {
        setVisibleMessages(prev => {
          const newMessages = [...prev];
          newMessages.shift();
          newMessages.push(allMessages[messageIndex]);
          return newMessages;
        });
        messageIndex++;
      }
      
      // Check if loading is complete
      if (currentProgress >= 100) {
        clearInterval(interval);
        setLoadingComplete(true);
      }
    }, stepTime);
    
    return () => clearInterval(interval);
  }, []);
  
  // Generate progress bar segments
  const getProgressBar = () => {
    const totalSegments = isMobile ? 25 : 50;
    const filledSegments = Math.floor((progress / 100) * totalSegments);
    return '█'.repeat(filledSegments) + '▒'.repeat(totalSegments - filledSegments);
  };

  const handleStartClick = () => {
    onLoadComplete();
  };

  const handleMobileRedirect = () => {
    window.location.href = 'https://os.varshithkarkera.in';
  };
  
  return (
    <div style={styles.container}>      
      {/* Boot text and logo */}
      <div style={styles.content}>
        {isMobile && (
          <div style={styles.mobileWarning}>
            WARNING: This experience is best viewed on a desktop or laptop computer.
          </div>
        )}
        
        <div style={styles.logoContainer}>
          <div style={styles.logo}>
            <div>
              <span style={styles.logoText}>VK-OS</span>
              <span style={styles.logoVersion}>08</span>
            </div>
            <div style={styles.logoSubtext}>X Inc</div>
          </div>
        </div>
        
        <div style={styles.messageArea}>
          {visibleMessages.map((message, index) => (
            <motion.p 
              key={`${message}-${index}`}
              style={styles.bootMessage}
              initial={{ opacity: index === visibleMessages.length - 1 ? 0 : 1 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {message}
            </motion.p>
          ))}
        </div>
        
        <div style={styles.loadingContainer}>
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ repeat: Infinity, duration: 2 }}
            style={styles.loadingText}
          >
            Loading VK-OS 08{!loadingComplete ? dots : ''}
          </motion.div>
          
          <div style={styles.progressBarContainer}>
            <div style={styles.progressLabel}>
              [{getProgressBar()}]
            </div>
            <div style={styles.progressPercentage}>
              {progress}%
            </div>
          </div>
        </div>
        
        <div style={styles.footer}>
          <div style={styles.buttonContainer}>
            {loadingComplete && (
              <>
                {isMobile && !forceDesktop && (
                  <div 
                    style={isHovering ? {...styles.mobileButton, ...styles.mobileButtonHover} : styles.mobileButton} 
                    onClick={handleMobileRedirect}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    <p style={styles.mobileButtonText}>
                      MOBILE VIEW
                    </p>
                  </div>
                )}
                <div 
                  style={!isMobile && isHovering ? {...styles.startButton, ...styles.startButtonHover} : styles.startButton} 
                  onClick={handleStartClick}
                  onMouseEnter={() => setIsHovering(true)}
                  onMouseLeave={() => setIsHovering(false)}
                >
                  <p style={!isMobile && isHovering ? {...styles.buttonText, ...styles.buttonTextHover} : styles.buttonText}>
                    {isMobile ? 'DESKTOP VIEW' : 'START'}
                  </p>
                </div>
              </>
            )}
          </div>
          
          <p style={styles.copyright}>© {new Date().getFullYear()} Varshith Karkera</p>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000000',
    overflow: 'auto',
    fontFamily: 'monospace',
    color: '#00ff41',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    boxSizing: 'border-box',
    zIndex: 9999,
  },
  content: {
    width: '100%',
    maxWidth: '800px',
    zIndex: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '20px',
  },
  logoContainer: {
    marginBottom: '20px',
  },
  logo: {
    border: '2px solid #00ff41',
    padding: '8px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  logoText: {
    fontSize: '32px',
    fontWeight: 'bold',
    letterSpacing: '2px',
  },
  logoVersion: {
    fontSize: '12px',
    verticalAlign: 'super',
    marginLeft: '4px',
  },
  logoSubtext: {
    fontSize: '16px',
    marginTop: '4px',
    color: '#00ff41',
    textAlign: 'center',
  },
  messageArea: {
    width: '100%',
    marginBottom: '30px',
    background: 'rgba(0, 20, 0, 0.5)',
    border: '1px solid #00ff41',
    padding: '15px',
    textAlign: 'left',
    height: '130px',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
  },
  bootMessage: {
    margin: '5px 0',
    fontSize: '14px',
    color: '#00ff41',
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
  },
  loadingText: {
    fontSize: '18px',
    marginBottom: '10px',
    letterSpacing: '1px',
  },
  progressBarContainer: {
    width: '100%',
  },
  progressLabel: {
    textAlign: 'center',
    fontSize: '16px',
    fontFamily: 'monospace',
    letterSpacing: '1px',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
  },
  progressPercentage: {
    textAlign: 'center',
    fontSize: '16px',
    marginTop: '8px',
    fontFamily: 'monospace',
    letterSpacing: '1px',
  },
  footer: {
    width: '100%',
    marginTop: '10px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  copyright: {
    fontSize: '14px',
    color: '#00ff41',
    marginTop: '16px',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    zIndex: 100,
    height: '120px',
  },
  mobileButton: {
    backgroundColor: '#00ff41',
    padding: '12px 24px',
    border: '2px solid #00ff41',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '160px',
    textAlign: 'center',
    position: 'relative',
    zIndex: 101,
  },
  mobileButtonHover: {
    backgroundColor: '#00cc33',
    borderColor: '#00cc33',
  },
  startButton: {
    backgroundColor: 'transparent',
    padding: '12px 24px',
    border: '2px solid #00ff41',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '160px',
    textAlign: 'center',
    position: 'relative',
    zIndex: 101,
  },
  startButtonHover: {
    backgroundColor: '#00ff41',
  },
  buttonText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#00ff41',
    margin: 0,
    pointerEvents: 'none',
  },
  buttonTextHover: {
    color: '#000000',
  },
  mobileButtonText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#000000',
    margin: 0,
    pointerEvents: 'none',
  },
  mobileWarning: {
    backgroundColor: '#ffcc00',
    color: '#000000',
    padding: '10px',
    textAlign: 'center',
    width: '100%',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: 'bold',
  },
};

export default RetroLoadingScreen; 
